import { calendarDateType } from "../../ControlPanel/_shared";
import type { PlayerNotesComponentSpec, MetricOption, SubjectEntity } from "../../Processing/types";
import type { DateRangeFormFields } from "../shared/DateRangeForm";
import type { PlayersFormFields } from "../shared/PlayersForm";
import { DATE_RANGE_DEFAULTS } from "../shared/DateRangeForm";
import { PLAYERS_DEFAULTS, resolveSubjectNames } from "../shared/PlayersForm";

// ── State ──

export interface PlayerNotesFormState extends DateRangeFormFields, PlayersFormFields {
  queryId: string;
  label: string;
  noteIds: string[];
  searchPattern: string;
  excludeInjuredPlayers: boolean;
  expandedKey: string | string[];
}

// ── Actions ──

export type PlayerNotesAction =
  | { type: "SET_LABEL"; label: string }
  | { type: "SET_NOTE_IDS"; noteIds: string[] }
  | { type: "SET_SEARCH_PATTERN"; pattern: string }
  | { type: "SET_EXCLUDE_INJURED"; value: boolean }
  | { type: "SET_EXPANDED_KEY"; key: string | string[] }
  | { type: "UPDATE_DATE_RANGE"; patch: Partial<DateRangeFormFields> }
  | { type: "UPDATE_PLAYERS"; patch: Partial<PlayersFormFields> };

// ── Reducer ──

export function playerNotesReducer(state: PlayerNotesFormState, action: PlayerNotesAction): PlayerNotesFormState {
  switch (action.type) {
    case "SET_LABEL": return { ...state, label: action.label };
    case "SET_NOTE_IDS": return { ...state, noteIds: action.noteIds };
    case "SET_SEARCH_PATTERN": return { ...state, searchPattern: action.pattern };
    case "SET_EXCLUDE_INJURED": return { ...state, excludeInjuredPlayers: action.value };
    case "SET_EXPANDED_KEY": return { ...state, expandedKey: action.key };
    case "UPDATE_DATE_RANGE": return { ...state, ...action.patch };
    case "UPDATE_PLAYERS": return { ...state, ...action.patch };
    default: return state;
  }
}

// ── Factories ──

export function createInitialState(): PlayerNotesFormState {
  return {
    queryId: crypto.randomUUID(),
    label: "Player Notes",
    noteIds: [],
    searchPattern: "",
    excludeInjuredPlayers: false,
    expandedKey: ["__date_range"],
    ...DATE_RANGE_DEFAULTS,
    ...PLAYERS_DEFAULTS,
  };
}

export function hydrateFromSpec(spec: PlayerNotesComponentSpec): PlayerNotesFormState {
  const misc = spec.player_notes_builder_misc;
  return {
    queryId: spec.queryId ?? crypto.randomUUID(),
    label: spec.display?.title || "Player Notes",
    noteIds: misc?.noteIds ?? [],
    searchPattern: misc?.searchPattern ?? "",
    excludeInjuredPlayers: misc?.excludeInjuredPlayers ?? false,
    expandedKey: [],
    dateRange: misc?.dateRange ?? DATE_RANGE_DEFAULTS.dateRange,
    showAdvancedDate: misc?.showAdvancedDate ?? DATE_RANGE_DEFAULTS.showAdvancedDate,
    startDateType: misc?.startDateType ?? DATE_RANGE_DEFAULTS.startDateType,
    startDateAbsolute: misc?.startDateAbsolute ?? DATE_RANGE_DEFAULTS.startDateAbsolute,
    startDateOffset: misc?.startDateOffset ?? DATE_RANGE_DEFAULTS.startDateOffset,
    endDateType: misc?.endDateType ?? DATE_RANGE_DEFAULTS.endDateType,
    endDateAbsolute: misc?.endDateAbsolute ?? DATE_RANGE_DEFAULTS.endDateAbsolute,
    endDateOffset: misc?.endDateOffset ?? DATE_RANGE_DEFAULTS.endDateOffset,
    dayFilter: misc?.dayFilter ?? DATE_RANGE_DEFAULTS.dayFilter,
    dateControllerId: misc?.dateControllerId,
    subjectControllerId: misc?.subjectControllerId,
    subjectControllerMode: misc?.subjectControllerMode,
    selectedSubjects: misc?.selectedSubjects ?? PLAYERS_DEFAULTS.selectedSubjects,
    selectedDrills: [],
    subjectActiveTab: "athletes",
  };
}

export function formStateToSpec(
  state: PlayerNotesFormState,
  _metrics: MetricOption[],
  getSubjectEntities?: (t: string) => SubjectEntity[],
): PlayerNotesComponentSpec {
  const selectedSubjectNames = getSubjectEntities
    ? resolveSubjectNames(state.selectedSubjects, [], getSubjectEntities)
    : [];

  return {
    type: "player_notes",
    display: { title: state.label || null },
    queryId: state.queryId,
    player_notes_builder_misc: {
      noteIds: state.noteIds,
      searchPattern: state.searchPattern || undefined,
      excludeInjuredPlayers: state.excludeInjuredPlayers === true ? true : undefined,
      dateRange: state.dateRange,
      showAdvancedDate: state.showAdvancedDate,
      startDateType: calendarDateType(state.startDateType),
      startDateAbsolute: state.startDateAbsolute,
      startDateOffset: state.startDateOffset,
      endDateType: state.endDateType,
      endDateAbsolute: state.endDateAbsolute,
      endDateOffset: state.endDateOffset,
      dayFilter: state.dayFilter,
      dateControllerId: state.dateControllerId,
      subjectControllerId: state.subjectControllerId,
      subjectControllerMode: state.subjectControllerMode,
      selectedSubjects: state.selectedSubjects,
      selectedSubjectNames,
    },
  };
}
